// ============================================================
// CUSTOMER DETAILS — Full drill-down ecosystem for a customer
// Route: /admin/customers/:id
// ============================================================

import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, User, ShoppingBag, BarChart3, Headphones,
  Activity, Phone, Mail, MapPin, Calendar, Clock,
  DollarSign, TrendingUp, Star, AlertTriangle, CheckCircle,
  XCircle, RefreshCw, Percent,
} from 'lucide-react';
import { Badge, Button, StatCard, Avatar, SearchInput } from '../components/ui';
import { adminApi } from '../services/apiService';
import { formatDate, formatCurrency } from '../utils/helpers';
import { toast } from 'react-toastify';
import { ORDER_STATUS_COLORS } from '../utils/constants';

// ── Shared helpers ────────────────────────────────────────────
const Spinner = ({ text = 'Loading…' }) => (
  <div style={{ textAlign: 'center', padding: 60, color: 'var(--admin-text-muted)' }}>
    <div style={{ width: 36, height: 36, border: '3px solid rgba(232,93,4,0.3)', borderTopColor: '#E85D04', borderRadius: '50%', animation: 'admin-spin 0.8s linear infinite', margin: '0 auto 12px' }} />
    <p>{text}</p>
  </div>
);

const Empty = ({ icon: Icon, title, subtitle }) => (
  <div style={{ textAlign: 'center', padding: 60, color: 'var(--admin-text-muted)' }}>
    {Icon && <Icon size={44} style={{ opacity: 0.25, marginBottom: 12 }} />}
    <p style={{ fontWeight: 700, fontSize: 15 }}>{title}</p>
    {subtitle && <p style={{ fontSize: 12, marginTop: 4 }}>{subtitle}</p>}
  </div>
);

const InfoRow = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '10px 0', borderBottom: '1px solid var(--admin-border)' }}>
    <span style={{ color: 'var(--admin-text-muted)', fontSize: 13, minWidth: 180 }}>{label}</span>
    <span style={{ fontWeight: 600, fontSize: 13, textAlign: 'right' }}>{value ?? '—'}</span>
  </div>
);

const TabButton = ({ active, onClick, icon: Icon, label }) => (
  <button onClick={onClick} style={{
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '10px 18px', cursor: 'pointer',
    background: 'none', border: 'none',
    borderBottom: active ? '2px solid #E85D04' : '2px solid transparent',
    color: active ? '#E85D04' : 'var(--admin-text-muted)',
    fontWeight: active ? 700 : 500, fontSize: 13,
    whiteSpace: 'nowrap',
  }}>
    {Icon && <Icon size={15} />}
    {label}
  </button>
);

// ─────────────────────────────────────────────────────────────
// TAB 1: PROFILE
// ─────────────────────────────────────────────────────────────
const ProfileTab = ({ customer, ordersCount = 0, totalSpent = 0 }) => {
  if (!customer) return <Empty icon={User} title="No profile data available" />;
  const c = customer;

  return (
    <div>
      {/* Hero */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 20,
        padding: 20, borderRadius: 12,
        background: 'linear-gradient(135deg, rgba(59,130,246,0.08), rgba(139,92,246,0.04))',
        border: '1px solid rgba(59,130,246,0.15)',
        marginBottom: 24,
      }}>
        <Avatar name={c.name || c.email} size={72} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 22, fontWeight: 900, marginBottom: 4 }}>{c.name || 'Anonymous'}</div>
          <div style={{ fontSize: 13, color: 'var(--admin-text-muted)', marginBottom: 8 }}>
            <Mail size={12} style={{ display: 'inline', marginRight: 4 }} />{c.email}
            &nbsp;•&nbsp;
            <Phone size={12} style={{ display: 'inline', marginRight: 4 }} />{c.phone || 'N/A'}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Badge status={c.status} />
            <span style={{ fontSize: 11, color: 'var(--admin-text-muted)' }}>
              <Calendar size={11} style={{ display: 'inline', marginRight: 3 }} />
              Joined {formatDate(c.createdAt || c.joinedAt)}
            </span>
          </div>
        </div>
        <Button
          variant={c.status === 'active' ? 'warning' : 'success'}
          size="sm"
          onClick={() => toast.info('Status update coming soon')}
        >
          {c.status === 'active' ? 'Disable Account' : 'Enable Account'}
        </Button>
      </div>

      {/* Metrics */}
      <div className="admin-stat-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 24 }}>
        <StatCard label="Total Orders"  value={ordersCount.toLocaleString()}                  icon={<ShoppingBag size={18} />} color="#E85D04" />
        <StatCard label="Total Spent"   value={formatCurrency(totalSpent || c.totalSpent || 0)} icon={<DollarSign size={18} />}  color="#10B981" />
        <StatCard label="Wallet Balance" value={formatCurrency(c.walletBalance ?? 0)}           icon={<DollarSign size={18} />}  color="#3B82F6" />
        <StatCard label="Reward Points" value={`⭐ ${c.rewardPoints ?? 0}`}                    icon={<Star size={18} />}        color="#F59E0B" />
      </div>

      {/* Two Column Info */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <div className="admin-card">
          <div className="admin-card-header"><h3 className="admin-card-title">Personal Information</h3></div>
          <div className="admin-card-body">
            <InfoRow label="Customer ID" value={<code style={{ fontSize: 11, background: 'var(--admin-bg)', padding: '2px 6px', borderRadius: 4 }}>{c.id}</code>} />
            <InfoRow label="Full Name" value={c.name} />
            <InfoRow label="Email" value={c.email} />
            <InfoRow label="Phone" value={c.phone} />
            <InfoRow label="City" value={c.city} />
            <InfoRow label="Status" value={<Badge status={c.status} />} />
            <InfoRow label="Joined Date" value={formatDate(c.createdAt || c.joinedAt)} />
            <InfoRow label="Last Order" value={c.lastOrder ? formatDate(c.lastOrder) : 'N/A'} />
          </div>
        </div>

        <div className="admin-card">
          <div className="admin-card-header"><h3 className="admin-card-title">Saved Addresses</h3></div>
          <div className="admin-card-body">
            {c.addresses && c.addresses.length > 0 ? (
              c.addresses.map((addr, i) => (
                <div key={i} style={{
                  padding: '10px 0', borderBottom: '1px solid var(--admin-border)',
                  display: 'flex', gap: 10, alignItems: 'flex-start',
                }}>
                  <MapPin size={14} style={{ color: '#E85D04', marginTop: 2, flexShrink: 0 }} />
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{addr.label || `Address ${i + 1}`}</div>
                    <div style={{ fontSize: 12, color: 'var(--admin-text-muted)' }}>{addr.full || addr.address}</div>
                  </div>
                </div>
              ))
            ) : (
              <div style={{ textAlign: 'center', padding: 24, color: 'var(--admin-text-muted)' }}>
                <MapPin size={28} style={{ opacity: 0.2, marginBottom: 8 }} />
                <p style={{ fontSize: 12 }}>No saved addresses</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── normalise a raw order object from the admin API ──────────
const normalizeOrder = (o) => ({
  ...o,
  pickupToken:    o.pickupToken    || o.pickup_token  || '',
  restaurantName: o.restaurantName || o.restaurant_name || o.restaurant || '—',
  customerName:   o.customerName   || o.customer_name  || o.customer   || '—',
  total:          o.total          || o.totalAmount    || o.amount      || 0,
  paymentMethod:  (o.paymentMethod || o.payment_method || '—').replace(/_/g, ' '),
  paymentStatus:  o.paymentStatus  || o.payment_status || 'pending',
  createdAt:      o.createdAt      || o.created_at     || o.placedAt    || o.placed_at || '',
  itemsSummary:   (o.itemsSummary && o.itemsSummary !== 'No items') ? o.itemsSummary : ((o.items_summary && o.items_summary !== 'No items') ? o.items_summary : ''),
  itemCount:      typeof o.items === 'number' ? o.items : (Array.isArray(o.items) ? o.items.length : 0),
  items:          Array.isArray(o.items) ? o.items : [],
});

// ─────────────────────────────────────────────────────────────
// TAB 2: ORDERS
// ─────────────────────────────────────────────────────────────
const OrderStatusBadge = ({ status }) => (
  <span style={{
    display: 'inline-block',
    fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
    background: `${ORDER_STATUS_COLORS[status] || '#94A3B8'}22`,
    color: ORDER_STATUS_COLORS[status] || '#94A3B8',
    border: `1px solid ${ORDER_STATUS_COLORS[status] || '#94A3B8'}44`,
    textTransform: 'capitalize',
  }}>{status}</span>
);

const OrdersTab = ({ orders, loading }) => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);

  const filtered = orders.filter(o => {
    const matchSearch = !search
      || (o.id || '').toLowerCase().includes(search.toLowerCase())
      || (o.restaurantName || '').toLowerCase().includes(search.toLowerCase())
      || (o.pickupToken || '').toLowerCase().includes(search.toLowerCase());
    const matchStatus = !statusFilter || o.status === statusFilter;
    return matchSearch && matchStatus;
  });

  if (loading) return <Spinner />;

  const statuses = ['new', 'accepted', 'preparing', 'ready', 'completed', 'cancelled', 'rejected'];

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <SearchInput value={search} onChange={setSearch} placeholder="Search by order ID, token, restaurant…" />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--admin-border)', background: 'var(--admin-surface)', color: 'var(--admin-text-primary)', fontSize: 13 }}>
          <option value="">All Status</option>
          {statuses.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
        </select>
        <span style={{ fontSize: 12, color: 'var(--admin-text-muted)', marginLeft: 'auto' }}>{filtered.length} orders</span>
      </div>

      {filtered.length === 0 ? (
        <Empty icon={ShoppingBag} title="No orders found" subtitle="This customer hasn't placed any orders yet." />
      ) : (
        <div style={{ borderRadius: 10, border: '1px solid var(--admin-border)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--admin-bg)' }}>
                {['Token', 'Restaurant', 'Items', 'Amount', 'Payment', 'Status', 'Date', ''].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, color: 'var(--admin-text-secondary)', borderBottom: '1px solid var(--admin-border)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((o, i) => (
                <tr key={o.id || i} style={{ borderBottom: '1px solid var(--admin-border)', cursor: 'pointer' }}
                  onClick={() => setSelectedOrder(o)}>
                  {/* Token */}
                  <td style={{ padding: '12px 16px' }}>
                    {o.pickupToken
                      ? <span style={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 700, background: 'rgba(232,93,4,0.1)', color: '#E85D04', padding: '2px 8px', borderRadius: 6 }}>#{o.pickupToken}</span>
                      : <code style={{ fontSize: 11, background: 'var(--admin-bg)', padding: '2px 6px', borderRadius: 4 }}>{(o.id || '').slice(0, 12)}…</code>}
                  </td>
                  {/* Restaurant */}
                  <td style={{ padding: '12px 16px', fontWeight: 600 }}>{o.restaurantName}</td>
                  {/* Items */}
                  <td style={{ padding: '12px 16px', color: 'var(--admin-text-muted)', fontSize: 12 }}>
                    {o.itemsSummary
                      ? o.itemsSummary
                      : o.itemCount
                        ? `${o.itemCount} item${o.itemCount > 1 ? 's' : ''}`
                        : '—'}
                  </td>
                  {/* Amount */}
                  <td style={{ padding: '12px 16px', fontWeight: 700, color: '#10B981' }}>{formatCurrency(o.total)}</td>
                  {/* Payment */}
                  <td style={{ padding: '12px 16px', fontSize: 12, textTransform: 'capitalize' }}>{o.paymentMethod}</td>
                  {/* Status */}
                  <td style={{ padding: '12px 16px' }}><OrderStatusBadge status={o.status} /></td>
                  {/* Date */}
                  <td style={{ padding: '12px 16px', color: 'var(--admin-text-muted)', fontSize: 12 }}>
                    {o.createdAt ? formatDate(o.createdAt) : 'N/A'}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ color: '#E85D04', fontSize: 12, fontWeight: 600 }}>View →</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Order Detail Modal — portalled to body so it covers the full screen including sidebar */}
      {selectedOrder && ReactDOM.createPortal(
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 20,
        }} onClick={() => setSelectedOrder(null)}>
          <div style={{
            background: 'var(--admin-surface)', borderRadius: 16, width: '100%', maxWidth: 520,
            maxHeight: '85vh', overflowY: 'auto', padding: 28,
            boxShadow: '0 25px 60px rgba(0,0,0,0.5)',
          }} onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ fontWeight: 800, fontSize: 17 }}>Order Details</h2>
              <button onClick={() => setSelectedOrder(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--admin-text-muted)', fontSize: 22, lineHeight: 1 }}>✕</button>
            </div>

            {/* Token + Status row */}
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 20, flexWrap: 'wrap' }}>
              {selectedOrder.pickupToken && (
                <span style={{ fontFamily: 'monospace', fontWeight: 800, background: 'rgba(232,93,4,0.12)', color: '#E85D04', padding: '5px 14px', borderRadius: 8, fontSize: 14, border: '1px solid rgba(232,93,4,0.25)' }}>
                  #{selectedOrder.pickupToken}
                </span>
              )}
              <OrderStatusBadge status={selectedOrder.status} />
            </div>

            <InfoRow label="Order ID"       value={<code style={{ fontSize: 11, wordBreak: 'break-all' }}>{selectedOrder.id}</code>} />
            <InfoRow label="Restaurant"     value={selectedOrder.restaurantName} />
            <InfoRow label="Total Amount"   value={<b style={{ color: '#10B981', fontSize: 14 }}>{formatCurrency(selectedOrder.total)}</b>} />
            <InfoRow label="Payment Method" value={<span style={{ textTransform: 'capitalize' }}>{selectedOrder.paymentMethod}</span>} />
            <InfoRow label="Payment Status" value={<OrderStatusBadge status={selectedOrder.paymentStatus} />} />
            <InfoRow label="Placed At"      value={selectedOrder.createdAt ? formatDate(selectedOrder.createdAt) : 'N/A'} />
            {selectedOrder.cancellationReason && (
              <InfoRow label="Cancelled Because" value={<span style={{ color: '#EF4444' }}>{selectedOrder.cancellationReason}</span>} />
            )}
            {selectedOrder.rejectionReason && (
              <InfoRow label="Rejected Because" value={<span style={{ color: '#DC2626' }}>{selectedOrder.rejectionReason}</span>} />
            )}

            {/* Items Ordered */}
            <div style={{ marginTop: 20 }}>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10, color: 'var(--admin-text-secondary)' }}>
                Items Ordered
              </div>
              {selectedOrder.items.length > 0 ? (
                selectedOrder.items.map((item, i) => {
                  const cookingReq = (item.cooking_request || item.special_instructions || '').trim();
                  return (
                  <div key={i} style={{ padding: '8px 0', borderBottom: '1px solid var(--admin-border)', fontSize: 13 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontWeight: 500 }}>{item.name || item.item_name} <span style={{ color: 'var(--admin-text-muted)' }}>× {item.quantity || 1}</span></span>
                      <span style={{ fontWeight: 700, color: '#10B981' }}>{formatCurrency((item.price || 0) * (item.quantity || 1))}</span>
                    </div>
                    {cookingReq ? (
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 5, marginTop: 4, background: '#FFF8F5', borderLeft: '3px solid #FF6B35', borderRadius: 5, padding: '3px 8px' }}>
                        <span style={{ fontSize: 12 }}>🍳</span>
                        <span style={{ fontSize: 12, color: '#CC5500', fontStyle: 'italic' }}>{cookingReq}</span>
                      </div>
                    ) : null}
                  </div>
                  );
                })
              ) : selectedOrder.itemsSummary ? (
                <div style={{ background: 'var(--admin-bg)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'var(--admin-text-primary)', fontWeight: 500 }}>
                  {selectedOrder.itemsSummary}
                </div>
              ) : (
                <div style={{ padding: '10px 0', fontSize: 13, color: 'var(--admin-text-muted)' }}>No item details available</div>
              )}
            </div>

          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// TAB 3: ANALYTICS
// ─────────────────────────────────────────────────────────────
const AnalyticsTab = ({ customer }) => {
  const c = customer || {};
  const avgOrderValue = c.totalOrders > 0 ? (c.totalSpent ?? 0) / c.totalOrders : 0;

  const metrics = [
    { label: 'Total Spend', value: formatCurrency(c.totalSpent ?? 0), color: '#10B981', icon: <DollarSign size={18} /> },
    { label: 'Avg Order Value', value: formatCurrency(avgOrderValue), color: '#3B82F6', icon: <TrendingUp size={18} /> },
    { label: 'Total Orders', value: (c.totalOrders ?? 0).toLocaleString(), color: '#E85D04', icon: <ShoppingBag size={18} /> },
    { label: 'Reward Points', value: `⭐ ${c.rewardPoints ?? 0}`, color: '#F59E0B', icon: <Star size={18} /> },
    { label: 'Cancellation Rate', value: `${c.cancellationRate ?? 0}%`, color: '#EF4444', icon: <XCircle size={18} /> },
    { label: 'Refund Rate', value: `${c.refundRate ?? 0}%`, color: '#F59E0B', icon: <AlertTriangle size={18} /> },
    { label: 'Wallet Balance', value: formatCurrency(c.walletBalance ?? 0), color: '#8B5CF6', icon: <DollarSign size={18} /> },
    { label: 'Lifetime Value', value: formatCurrency(c.totalSpent ?? 0), color: '#10B981', icon: <Percent size={18} /> },
  ];

  return (
    <div>
      <div className="admin-stat-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 24 }}>
        {metrics.slice(0, 4).map(m => <StatCard key={m.label} label={m.label} value={m.value} icon={m.icon} color={m.color} />)}
      </div>
      <div className="admin-stat-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 24 }}>
        {metrics.slice(4).map(m => <StatCard key={m.label} label={m.label} value={m.value} icon={m.icon} color={m.color} />)}
      </div>

      {/* Favorite restaurant & cuisine */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <div className="admin-card">
          <div className="admin-card-header"><h3 className="admin-card-title">Favorite Restaurant</h3></div>
          <div className="admin-card-body">
            {c.favoriteRestaurant ? (
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: 12, borderRadius: 8, background: 'var(--admin-bg)' }}>
                <Avatar name={c.favoriteRestaurant} size={44} />
                <div>
                  <div style={{ fontWeight: 700 }}>{c.favoriteRestaurant}</div>
                  <div style={{ fontSize: 12, color: 'var(--admin-text-muted)' }}>Most ordered from</div>
                </div>
              </div>
            ) : (
              <Empty icon={Star} title="No data yet" />
            )}
          </div>
        </div>
        <div className="admin-card">
          <div className="admin-card-header"><h3 className="admin-card-title">Favorite Cuisine</h3></div>
          <div className="admin-card-body">
            {c.favoriteCuisine ? (
              <div style={{ textAlign: 'center', padding: 20 }}>
                <div style={{ fontSize: 48, marginBottom: 8 }}>🍛</div>
                <div style={{ fontWeight: 700, fontSize: 18 }}>{c.favoriteCuisine}</div>
              </div>
            ) : (
              <Empty icon={Star} title="No data yet" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// TAB 4: SUPPORT & COMPLAINTS
// ─────────────────────────────────────────────────────────────
const SupportTab = ({ customerId }) => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi.getSupportTickets({ customer_id: customerId }, () => [])
      .then(res => {
        const arr = Array.isArray(res) ? res : (res?.items || []);
        const filtered = arr.filter(t => t.customerId === customerId || t.customer_id === customerId);
        setTickets(filtered);
        setLoading(false);
      })
      .catch(() => { setTickets([]); setLoading(false); });
  }, [customerId]);

  if (loading) return <Spinner />;
  if (tickets.length === 0) return <Empty icon={Headphones} title="No support tickets" subtitle="No complaints or tickets filed by this customer." />;

  const priorityColor = { high: '#EF4444', medium: '#F59E0B', low: '#10B981' };

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', gap: 12 }}>
        <span style={{ padding: '4px 12px', borderRadius: 6, background: '#FEE2E2', color: '#991B1B', fontSize: 12, fontWeight: 700 }}>
          Open: {tickets.filter(t => t.status === 'open').length}
        </span>
        <span style={{ padding: '4px 12px', borderRadius: 6, background: '#D1FAE5', color: '#065F46', fontSize: 12, fontWeight: 700 }}>
          Resolved: {tickets.filter(t => t.status === 'resolved').length}
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {tickets.map((t, i) => (
          <div key={t.id || i} style={{
            padding: 16, borderRadius: 10, border: '1px solid var(--admin-border)',
            background: 'var(--admin-surface)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontWeight: 700, fontSize: 13 }}>{t.subject || t.title || 'Support Ticket'}</span>
                {t.priority && (
                  <span style={{ fontSize: 11, fontWeight: 700, padding: '1px 8px', borderRadius: 4, background: `${priorityColor[t.priority]}22`, color: priorityColor[t.priority] }}>
                    {t.priority}
                  </span>
                )}
              </div>
              <Badge status={t.status} />
            </div>
            <p style={{ fontSize: 12, color: 'var(--admin-text-muted)', margin: 0 }}>{t.message || t.description}</p>
            <div style={{ fontSize: 11, color: 'var(--admin-text-muted)', marginTop: 8 }}>
              Opened: {formatDate(t.createdAt || t.created_at)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// TAB 5: ACTIVITY LOGS
// ─────────────────────────────────────────────────────────────
const ActivityTab = ({ customerId }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi.getActivityLogs({ entity_type: 'customer', entity_id: customerId })
      .then(res => {
        const arr = Array.isArray(res) ? res : (res?.logs || res?.items || []);
        setLogs(arr);
        setLoading(false);
      })
      .catch(() => { setLogs([]); setLoading(false); });
  }, [customerId]);

  if (loading) return <Spinner />;
  if (logs.length === 0) return (
    <div>
      <Empty icon={Activity} title="No activity logs" subtitle="Login history, address changes and fraud alerts appear here." />
      {/* Placeholder device info */}
      <div style={{ marginTop: 24 }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>Device & Session Info</div>
        <div style={{ padding: 16, borderRadius: 10, border: '1px solid var(--admin-border)', background: 'var(--admin-surface)', color: 'var(--admin-text-muted)', fontSize: 13 }}>
          No device information available. Enable activity tracking to collect session data.
        </div>
      </div>
    </div>
  );

  const typeColor = { login: '#3B82F6', address_change: '#E85D04', fraud_alert: '#EF4444', profile_update: '#10B981' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {logs.map((log, i) => (
        <div key={log.id || i} style={{ padding: '14px 20px', borderBottom: '1px solid var(--admin-border)', display: 'flex', gap: 14, alignItems: 'flex-start' }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', marginTop: 6, flexShrink: 0, background: typeColor[log.type] || '#94A3B8' }} />
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <span style={{ fontWeight: 600, fontSize: 13 }}>{log.action || log.message}</span>
              <span style={{ fontSize: 11, color: 'var(--admin-text-muted)', flexShrink: 0 }}>{formatDate(log.created_at || log.timestamp)}</span>
            </div>
            {log.device && <div style={{ fontSize: 11, color: 'var(--admin-text-muted)', marginTop: 2 }}>Device: {log.device}</div>}
            {log.ip && <div style={{ fontSize: 11, color: 'var(--admin-text-muted)' }}>IP: {log.ip}</div>}
          </div>
        </div>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// MAIN CUSTOMER DETAILS PAGE
// ─────────────────────────────────────────────────────────────
const TABS = [
  { id: 'profile',   label: 'Profile',              icon: User },
  { id: 'orders',    label: 'Orders',               icon: ShoppingBag },
  { id: 'analytics', label: 'Analytics',            icon: BarChart3 },
  { id: 'support',   label: 'Support & Complaints', icon: Headphones },
  { id: 'activity',  label: 'Activity Logs',        icon: Activity },
];

const CustomerDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);

  // Load customer profile
  useEffect(() => {
    setLoading(true);
    adminApi.getCustomers({ id }, () => [])
      .then(res => {
        const arr = Array.isArray(res) ? res : (res?.items || []);
        const c = arr.find(x => x.id === id || String(x.id) === String(id));
        setCustomer(c || null);
        setLoading(false);
      })
      .catch(() => { setCustomer(null); setLoading(false); });
  }, [id]);

  // Load customer orders separately so count is always accurate
  useEffect(() => {
    setOrdersLoading(true);
    adminApi.getOrders({ customer_id: id, per_page: 500 }, () => [])
      .then(res => {
        const arr = Array.isArray(res) ? res : (res?.items || res?.orders || []);
        setOrders(arr.map(normalizeOrder));
        setOrdersLoading(false);
      })
      .catch(() => { setOrders([]); setOrdersLoading(false); });
  }, [id]);

  if (loading) return (
    <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Spinner text="Loading customer details..." />
    </div>
  );

  return (
    <div>
      {/* Back navigation */}
      <button onClick={() => navigate('/admin/customers')} style={{
        display: 'flex', alignItems: 'center', gap: 6,
        background: 'none', border: 'none', cursor: 'pointer',
        color: 'var(--admin-text-muted)', fontSize: 13, fontWeight: 600,
        padding: '0 0 16px', marginBottom: 4,
      }}>
        <ArrowLeft size={16} /> Back to Customers
      </button>

      {/* Page Header */}
      <div className="admin-page-header" style={{ marginBottom: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <Avatar name={customer?.name || customer?.email || 'C'} size={48} />
          <div>
            <h1 className="admin-page-title" style={{ marginBottom: 2 }}>{customer?.name || 'Customer'}</h1>
            <p className="admin-page-subtitle">{customer?.email} • ID: {id}</p>
          </div>
        </div>
        <div className="admin-page-actions">
          {customer && <Badge status={customer.status} />}
        </div>
      </div>

      {/* Tab Bar */}
      <div style={{
        display: 'flex', borderBottom: '1px solid var(--admin-border)',
        marginTop: 20, marginBottom: 24, overflowX: 'auto',
        scrollbarWidth: 'none',
      }}>
        {TABS.map(tab => (
          <TabButton
            key={tab.id}
            active={activeTab === tab.id}
            onClick={() => setActiveTab(tab.id)}
            icon={tab.icon}
            label={tab.label}
          />
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'profile'   && <ProfileTab customer={customer} ordersCount={orders.length} totalSpent={orders.reduce((s, o) => s + (o.total || 0), 0)} />}
      {activeTab === 'orders'    && <OrdersTab orders={orders} loading={ordersLoading} />}
      {activeTab === 'analytics' && <AnalyticsTab customer={customer} />}
      {activeTab === 'support'   && <SupportTab customerId={id} />}
      {activeTab === 'activity'  && <ActivityTab customerId={id} />}
    </div>
  );
};

export default CustomerDetails;
